import React, { useState, useEffect, useMemo } from "react";
import Footer from "../footer/footer";
import Header from "../header/header";
import CookieConsentHOC from "../../containers/CookieConsentHOC";
import ScrollToTop from "../scrollTop/scrollTop";
import Banner from "../banner/banner";
import TerminalHOC from "../../containers/terminal/terminalHOC/terminalHOC";
import DateTimePicker from "../datePicker/datePicker";
import ContentRouter from "../../routers/contentRouter";
import GettingStartedButton from "../gettingStartedButton/gettingStartButton";
import ProductTourContainer from "../productTour/productTourContainer";
import "./layout.css";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import OnboardingContainer from "../onBoarding/container/onBoardingContainer";
import useUser from "../../customHooks/useUser";
import { Toaster } from "react-hot-toast";
import ApiErrorToaster from "../apiErrorToaster/apiErrorToaster";

const Layout = (props) => {
  const { location } = props;
  const [showProductTour, setShowProductTour] = useState(false);
  const [storiesWithProgress, setStoriesWithProgress] = useState([]);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 992 : false
  );

  let history = useHistory();

  const gettingStartedData = useSelector(
    ({ gettingStarted }) => gettingStarted
  );

  let stories = useMemo(() => {
    return (
      gettingStartedData?.gettingStartedAllIds?.map(
        (id) => gettingStartedData?.gettingStartedById?.[id]
      ) || []
    );
  }, [
    gettingStartedData?.gettingStartedAllIds,
    gettingStartedData?.gettingStartedById,
  ]);

  let { isLoggedIn } = useUser();

  let completedStories = useSelector(
    ({ applicationUser }) => applicationUser?.completedStories
  );

  const layoutStyles = {
    width: showProductTour ? "75%" : "100%",
    transition: "width 0.2s ease-out",
  };

  // Track viewport size to adjust mobile-specific behavior
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleResize = () => setIsMobileViewport(window.innerWidth < 992);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initialize and manage stories state with localStorage persistence
  useEffect(() => {
    // Load persisted state from localStorage
    const storedStories = localStorage.getItem("gettingStartedStoriesState");
    if (storedStories) {
      const parsed = JSON.parse(storedStories);
      setStoriesWithProgress(parsed);
    } else if (stories.length > 0) {
      // Initialize all stories with "PENDING" completedCondition
      const initialStories = stories.map((story) => ({
        ...story,
        completedCondition: story.completedCondition || "PENDING",
      }));
      setStoriesWithProgress(initialStories);
    }
  }, [stories]);

  // Set up global function for productTour to update stories
  useEffect(() => {
    window.updateGettingStartedStories = (updatedStories) => {
      setStoriesWithProgress(updatedStories);
    };

    return () => {
      delete window.updateGettingStartedStories;
    };
  }, []);

  useEffect(() => {
    const { hash } = window.location;
    if (hash === "#quickstart" && localStorage.getItem("disableQuickStart")) {
      setShowProductTour(true);
      localStorage.removeItem("disableQuickStart");
    }
  });

  // Precompute route flags early so hooks can use them safely
  const isSwaggerPage =
    (location &&
      location.startsWith("/apiProduct/") &&
      location.includes("/swagger/")) ||
    (location && location.startsWith("/swagger"));
  const isApiProductsPage = location && location.startsWith("/apiProducts");
  const isBlogListPage =
    (location && location === "/blog") || (location && location === "/news");
  const isBlogSinglePage = location && location.startsWith("/news/");
  const isBlogPage = isBlogListPage || isBlogSinglePage;
  const isFaqPage = location && location === "/faq-page";
  const isSplitLayoutPage = isFaqPage || isBlogListPage || isApiProductsPage;
  const footerVariant = isSplitLayoutPage
    ? "short"
    : pageLoaded
    ? "short"
    : "tall";
  const shouldUseFixedFooter = isSplitLayoutPage && !isMobileViewport;

  // Track when the page has fully loaded (including assets)
  useEffect(() => {
    const markLoaded = () => setPageLoaded(true);
    if (document.readyState === "complete") {
      markLoaded();
    } else {
      window.addEventListener("load", markLoaded);
    }
    return () => window.removeEventListener("load", markLoaded);
  }, []);

  // Disable page scroll for FAQ, Blog list, and API Products pages only on desktop-sized viewports
  useEffect(() => {
    const shouldLockScroll =
      !isMobileViewport && (isFaqPage || isBlogListPage || isApiProductsPage);

    if (shouldLockScroll) {
      document.body.classList.add("faq-page-no-scroll");
    } else {
      document.body.classList.remove("faq-page-no-scroll");
    }

    return () => document.body.classList.remove("faq-page-no-scroll");
  }, [isFaqPage, isBlogListPage, isApiProductsPage, isMobileViewport]);

  if (location.indexOf("/partner") === 0) {
    const url = location.replace("/partner", "");
    history.push(url);
  }

  const isGoliveRoute = location.indexOf("/goLive") === 0;
  const handleCloseProductTour = () => {
    setShowProductTour(false);
    window.location.hash = "";
  };

  return (
    <>
      <div
        style={layoutStyles}
        className={showProductTour ? "layout-main tour-open" : "layout-main"}
      >
        <Header
          location={location}
          forcePinned={
            isSwaggerPage || isBlogListPage || isFaqPage || isApiProductsPage
          }
          disablePin={isBlogSinglePage}
          hidePinControl={
            isSwaggerPage || isBlogPage || isFaqPage || isApiProductsPage
          }
        />
        <div>
          <Toaster position="top-center" />
          <ApiErrorToaster />
        </div>
        <div className="layout-content">
          <ContentRouter />
        </div>
        {!isSwaggerPage && <CookieConsentHOC />}
        {!isSwaggerPage && <Banner />}
        {!isSwaggerPage &&
          !showProductTour &&
          !localStorage.getItem("disableQuickStart") && (
            <GettingStartedButton
              handleClick={() => setShowProductTour(true)}
            />
          )}

        {!isSwaggerPage && <TerminalHOC />}
        {!isSwaggerPage && <ScrollToTop />}
        {!isSwaggerPage && (
          <Footer
            {...props}
            variant={footerVariant}
            fixed={shouldUseFixedFooter}
          />
        )}
      </div>
      <ProductTourContainer
        isLoggedIn={isLoggedIn}
        stories={storiesWithProgress.length > 0 ? storiesWithProgress : stories}
        showProductTour={showProductTour}
        completedStories={completedStories}
        closeProductTour={handleCloseProductTour}
      />

      <OnboardingContainer
      // canClose={!isGoliveRoute}
      />
      <DateTimePicker />
    </>
  );
};

export default Layout;
